import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { trainingAPI, staffAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import { formatDisplayDate } from '../utils/dateFormat';
import './TrainingActivityReport.css';

function formatDate(d) {
  if (!d) return '—';
  return formatDisplayDate(d);
}

function parseHours(h) {
  const n = parseFloat(h, 10);
  return Number.isFinite(n) ? n : 0;
}

function timeKey(d) {
  if (!d) return 0;
  const t = new Date(d).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/** Compare type strings; empty/null last */
function compareType(a, b, dir) {
  const sa = (a ?? '').trim();
  const sb = (b ?? '').trim();
  const ea = !sa;
  const eb = !sb;
  if (ea && eb) return 0;
  if (ea) return 1;
  if (eb) return -1;
  const c = sa.localeCompare(sb, undefined, { sensitivity: 'base' });
  return dir === 'asc' ? c : -c;
}

function csvEscape(val) {
  if (val == null) return '';
  const s = String(val);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCsv(filename, headers, rows) {
  const line = (arr) => arr.map(csvEscape).join(',');
  const body = [line(headers), ...rows.map((r) => line(r))].join('\r\n');
  const blob = new Blob([body], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(a.href);
  document.body.removeChild(a);
}

export default function TrainingActivityReport({ embedded = false, refreshKey = 0, tenantCode = null }) {
  const [staffList, setStaffList] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  /** primary: 'type' = legacy (group + subtotals), 'date' = flat list + summary below */
  const [sort, setSort] = useState({ primary: 'type', dir: 'asc' });
  const canMonitor = hasPermission('training.monitor');

  const loadStaff = useCallback(async () => {
    if (!canMonitor) return;
    try {
      const res = await staffAPI.getAll({ active: 'true' });
      const data = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
      setStaffList(data);
      if (data.length && selectedStaffId == null) setSelectedStaffId(data[0].id);
    } catch {
      setStaffList([]);
    }
  }, [canMonitor, selectedStaffId]);

  const runReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = canMonitor && selectedStaffId != null ? { staff_id: selectedStaffId } : {};
      const res = await trainingAPI.reportActivity(params);
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load report');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [canMonitor, selectedStaffId]);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  useEffect(() => {
    runReport();
  }, [runReport, refreshKey]);

  const staffName = useCallback((row) => [row.staff_last_name, row.staff_first_name].filter(Boolean).join(', ') || '—', []);

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    const { primary, dir } = sort;
    const mul = dir === 'asc' ? 1 : -1;
    copy.sort((a, b) => {
      if (primary === 'date') {
        const da = timeKey(a.training_date);
        const db = timeKey(b.training_date);
        if (da !== db) return mul * (da - db);
        const tc = compareType(a.type, b.type, 'asc');
        if (tc !== 0) return tc;
        return String(a.activity || '').localeCompare(String(b.activity || ''));
      }
      const tc = compareType(a.type, b.type, dir);
      if (tc !== 0) return tc;
      const da = timeKey(a.training_date);
      const db = timeKey(b.training_date);
      if (da !== db) return da - db;
      return String(a.activity || '').localeCompare(String(b.activity || ''));
    });
    return copy;
  }, [rows, sort]);

  const { totalsByType, grandTotal, sortedTypeKeys } = useMemo(() => {
    const byType = new Map();
    let grand = 0;
    for (const r of rows) {
      const t = (r.type || '').trim() || '—';
      const h = parseHours(r.hours);
      byType.set(t, (byType.get(t) || 0) + h);
      grand += h;
    }
    const sortedTypes = [...byType.keys()].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    return { totalsByType: byType, grandTotal: grand, sortedTypeKeys: sortedTypes };
  }, [rows]);

  const grandTotalLabel = useMemo(() => {
    if (rows.length === 0) return 'Total';
    const names = new Set(rows.map((r) => staffName(r)));
    if (names.size === 1) return [...names][0];
    return 'Total';
  }, [rows, staffName]);

  const toggleSort = useCallback((col) => {
    setSort((prev) => {
      if (prev.primary !== col) {
        return { primary: col, dir: 'asc' };
      }
      return { primary: col, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    });
  }, []);

  const handleReset = useCallback(() => {
    setSort({ primary: 'type', dir: 'asc' });
    if (canMonitor && staffList.length > 0) {
      setSelectedStaffId(staffList[0].id);
    }
  }, [canMonitor, staffList]);

  const handleExportCsv = useCallback(() => {
    const headers = canMonitor ? ['Staff', 'Activity', 'Date', 'Hours', 'Type', 'Supporting doc'] : ['Activity', 'Date', 'Hours', 'Type', 'Supporting doc'];
    const rowData = sortedRows.map((row) => {
      const dateStr = row.training_date ? (typeof row.training_date === 'string' && row.training_date.length >= 10 ? row.training_date.slice(0, 10) : formatDisplayDate(row.training_date)) : '';
      const base = [row.activity || '', dateStr, row.hours ?? '', row.type || '', row.supporting_doc || ''];
      return canMonitor ? [staffName(row), ...base] : base;
    });
    const summaryLines = sortedTypeKeys.map((t) => {
      const h = totalsByType.get(t) || 0;
      return canMonitor
        ? ['', '', '', h.toFixed(2), `Subtotal: ${t}`, '']
        : ['', '', h.toFixed(2), `Subtotal: ${t}`, ''];
    });
    const totalLine = canMonitor
      ? ['', '', '', grandTotal.toFixed(2), grandTotalLabel, '']
      : ['', '', grandTotal.toFixed(2), grandTotalLabel, ''];
    downloadCsv('training-activity-report.csv', headers, [...rowData, ...summaryLines, totalLine]);
  }, [sortedRows, canMonitor, staffName, totalsByType, sortedTypeKeys, grandTotal, grandTotalLabel]);

  const tableBody = useMemo(() => {
    if (rows.length === 0) {
      return (
        <tr>
          <td colSpan={canMonitor ? 6 : 5}>No activities in current window.</td>
        </tr>
      );
    }

    const dataRow = (row) => (
      <tr key={row.id}>
        {canMonitor && <td>{staffName(row)}</td>}
        <td>{row.activity || '—'}</td>
        <td>{formatDate(row.training_date)}</td>
        <td>{row.hours ?? '—'}</td>
        <td>{row.type || '—'}</td>
        <td>{row.supporting_doc || '—'}</td>
      </tr>
    );

    const fragments = [];

    if (sort.primary === 'type') {
      let i = 0;
      while (i < sortedRows.length) {
        const groupType = (sortedRows[i].type || '').trim() || '—';
        let sub = 0;
        let j = i;
        while (j < sortedRows.length) {
          const rt = (sortedRows[j].type || '').trim() || '—';
          if (rt !== groupType) break;
          fragments.push(dataRow(sortedRows[j]));
          sub += parseHours(sortedRows[j].hours);
          j += 1;
        }
        fragments.push(
          <tr key={`subtotal-${groupType}-${i}`} className="training-report-subtotal">
            {canMonitor && <td aria-hidden="true" />}
            <td colSpan={2} />
            <td className="training-report-subtotal-hours">{sub.toFixed(2)}</td>
            <td className="training-report-subtotal-type">{groupType}</td>
            <td aria-hidden="true" />
          </tr>
        );
        i = j;
      }
    } else {
      sortedRows.forEach((row) => {
        fragments.push(dataRow(row));
      });
    }

    fragments.push(
      <tr key="grand-total" className="training-report-grand-total">
        {canMonitor && <td aria-hidden="true" />}
        <td colSpan={2} />
        <td>{grandTotal.toFixed(2)}</td>
        <td>{grandTotalLabel}</td>
        <td aria-hidden="true" />
      </tr>
    );

    return <>{fragments}</>;
  }, [rows.length, sortedRows, sort.primary, canMonitor, staffName, grandTotal, grandTotalLabel]);

  return (
    <div className="training-activity-report">
      <div className="training-report-toolbar no-print">
        <div className="training-report-toolbar-left">
          <h3 className="training-report-title">Training Activity Report{tenantCode ? ` — ${tenantCode}` : ''}</h3>
          {canMonitor && staffList.length > 0 && (
            <div className="training-report-filters">
              <label htmlFor="training-activity-staff">Staff: </label>
              <select
                id="training-activity-staff"
                value={selectedStaffId ?? ''}
                onChange={(e) => setSelectedStaffId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">All</option>
                {staffList.map((s) => (
                  <option key={s.id} value={s.id}>
                    {[s.last_name, s.first_name].filter(Boolean).join(', ')}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="training-report-toolbar-actions">
          <button type="button" className="btn btn-secondary" onClick={handleReset}>Reset</button>
          <button type="button" className="btn btn-secondary" onClick={handleExportCsv}>Export CSV</button>
          <button type="button" className="btn btn-primary" onClick={() => window.print()}>Print</button>
        </div>
      </div>
      <div className="training-report-print-header print-only">
        <h1>Training Activity Report{tenantCode ? ` — ${tenantCode}` : ''}</h1>
      </div>
      {error && <p className="training-report-error">{error}</p>}
      <div className="training-report-table-wrap">
        {loading && rows.length === 0 ? (
          <p>Loading...</p>
        ) : (
          <table className="training-report-table">
            <thead>
              <tr>
                {canMonitor && <th scope="col">Staff</th>}
                <th scope="col">Activity</th>
                <th scope="col" aria-sort={sort.primary === 'date' ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <button type="button" className="training-report-th-sort" onClick={() => toggleSort('date')}>
                    Date
                    {sort.primary === 'date' ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </button>
                </th>
                <th scope="col">Hours</th>
                <th scope="col" aria-sort={sort.primary === 'type' ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <button type="button" className="training-report-th-sort" onClick={() => toggleSort('type')}>
                    Type
                    {sort.primary === 'type' ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </button>
                </th>
                <th scope="col">Supporting doc</th>
              </tr>
            </thead>
            <tbody>{tableBody}</tbody>
          </table>
        )}
      </div>
      {sort.primary === 'date' && rows.length > 0 && (
        <div className="training-report-summary-wrap">
          <h4 className="training-report-summary-title">Subtotals by type</h4>
          <table className="training-report-table training-report-summary-table">
            <thead>
              <tr>
                <th scope="col">Type</th>
                <th scope="col">Hours</th>
              </tr>
            </thead>
            <tbody>
              {sortedTypeKeys.map((t) => (
                <tr key={t}>
                  <td>{t}</td>
                  <td>{(totalsByType.get(t) || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
